import { useState, type FormEvent } from "react";
import { ArrowRight, KeyRound, ShieldCheck } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { Button } from "../components/Button";
import { Logo } from "../components/Logo";

export function AuthScreen({
  needsSetup,
  onAuthenticated
}: {
  needsSetup: boolean;
  onAuthenticated: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (needsSetup && password !== confirmation) {
      setError("两次输入的密码不一致。");
      return;
    }
    if (password.length < 8) {
      setError("密码至少需要 8 个字符。");
      return;
    }
    setBusy(true);
    try {
      if (needsSetup) await api.setup(password);
      else await api.login(password);
      onAuthenticated();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "无法连接学习室服务。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-canvas">
      <div className="auth-atmosphere" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <section className="auth-story">
        <Logo />
        <div className="auth-story-copy">
          <span className="eyebrow">A PRIVATE LISTENING ROOM</span>
          <h1>
            把一场演讲，
            <br />
            听成自己的语言。
          </h1>
          <p>
            TED 的完整视频保存在你的私有云端。英语转写与中文翻译只属于你。
          </p>
        </div>
        <div className="auth-signature" aria-hidden="true">
          <span>LISTEN</span>
          <div>
            {Array.from({ length: 22 }, (_, index) => (
              <i key={index} style={{ "--i": index } as React.CSSProperties} />
            ))}
          </div>
          <span>UNDERSTAND</span>
        </div>
      </section>

      <section className="auth-panel">
        <div className="auth-card">
          <span className="auth-icon">
            {needsSetup ? <ShieldCheck size={24} /> : <KeyRound size={24} />}
          </span>
          <span className="eyebrow">
            {needsSetup ? "首次进入 · 建立私人入口" : "欢迎回来"}
          </span>
          <h2>{needsSetup ? "为学习室设置密码" : "打开你的学习室"}</h2>
          <p className="auth-hint">
            {needsSetup
              ? "这个应用只有你一个人使用。密码只保存为 Argon2id 哈希。"
              : "输入学习室密码，从手机或电脑继续你的精听进度。"}
          </p>

          <form onSubmit={submit} className="auth-form">
            <label>
              <span>{needsSetup ? "设置密码" : "密码"}</span>
              <input
                type="password"
                autoComplete={needsSetup ? "new-password" : "current-password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="至少 8 个字符"
                autoFocus
              />
            </label>
            {needsSetup && (
              <label>
                <span>再次输入</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  placeholder="确认同一个密码"
                />
              </label>
            )}
            {error && <p className="form-error">{error}</p>}
            <Button
              type="submit"
              variant="primary"
              loading={busy}
              icon={<ArrowRight size={17} />}
            >
              {needsSetup ? "建立学习室" : "进入"}
            </Button>
          </form>

          <div className="auth-footnote">
            <span className="status-dot" />
            单用户本地应用 · 不采集第三方数据
          </div>
        </div>
      </section>
    </div>
  );
}

